import { useAuth0 } from '@auth0/auth0-react';
import {
  ArrowRightIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  CheckIcon,
  ClipboardDocumentListIcon,
  FolderOpenIcon,
  PencilSquareIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { Internal } from '@gleamer/types';
import classNames from 'classnames';
import React from 'react';
import { Link } from 'react-router-dom';
import { useMyTasks } from '../../hooks/useMyTasks';
import { useLastNotifications } from '../../hooks/useNotifications';
import { usePermissions } from '../../hooks/usePermissions';
import { DATE_LOCALE } from '../../utils/locale';
import { BACKOFFICE_PERMISSIONS } from '../../utils/permissions';
import {
  byProgress,
  formatDay,
  isFinished,
  RoleChip,
  TaskAction,
  TaskProgress,
} from '../molecules/tasks/TaskCard';
import { TaskLink } from '../molecules/TaskLink';

function greeting(now = new Date()): string {
  const hour = now.getHours();
  return hour < 12
    ? 'Good morning'
    : hour < 18
      ? 'Good afternoon'
      : 'Good evening';
}

/**
 * The name to greet: the given name as Auth0 has it (accents and all), else the first word of the name or of
 * the e-mail's local part. A compound first name stays whole — `pierre-andre.geulin@…` is "Pierre-Andre" —
 * each of its parts capitalised; an e-mail cannot say more (no accent), only a given name can.
 */
export function firstName(
  user: { given_name?: string; name?: string; email?: string } | undefined
): string {
  const given = user?.given_name?.trim();
  if (given) {
    return given;
  }
  const source = user?.name ?? user?.email ?? '';
  const word = source.split('@')[0].split(/[\s._]/)[0];
  return word
    .split('-')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join('-');
}

/** A figure on the dashboard: a neutral tile, a dot of its role's colour, and the filtered list it opens. */
function Stat({
  label,
  value,
  icon: Icon,
  tone,
  to,
}: {
  label: string;
  value: React.ReactNode;
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  /** the icon's badge: a tint background and its colour */
  tone: string;
  to: string;
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-4 rounded-2xl border border-dl-border bg-dl-surface px-5 py-4 transition hover:border-dl-strong"
    >
      <span
        className={classNames(
          'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
          tone
        )}
      >
        <Icon aria-hidden className="h-6 w-6" />
      </span>
      <span className="flex flex-col gap-0.5">
        <span className="text-4xl font-semibold tabular-nums tracking-tight text-dl-text">
          {value}
        </span>
        <span className="text-lg text-dl-muted">{label}</span>
      </span>
    </Link>
  );
}

function Panel({
  title,
  action,
  flush,
  children,
}: React.PropsWithChildren<{
  title: string;
  action?: React.ReactNode;
  /** Rows that run to the card's edges, under a header with its own line. */
  flush?: boolean;
}>) {
  return (
    <section
      aria-label={title}
      className="overflow-hidden rounded-2xl border border-dl-border bg-dl-surface"
    >
      <div
        className={classNames('flex items-center justify-between px-6', {
          'border-b border-dl-border py-4': flush,
          'pb-3 pt-5': !flush,
        })}
      >
        <h2 className="text-xl font-semibold text-dl-text">{title}</h2>
        {action}
      </div>
      <div className={flush ? undefined : 'px-6 pb-5'}>{children}</div>
    </section>
  );
}

function SeeAll({
  to,
  children = 'See all',
}: React.PropsWithChildren<{ to: string }>) {
  return (
    <Link
      to={to}
      className="text-lg font-medium text-dl-accent hover:underline"
    >
      {children}
    </Link>
  );
}

const shortcut =
  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-lg font-medium text-dl-muted transition hover:bg-dl-sunken hover:text-dl-text';

/** The task to go back to: its name, role, what it is about, how far along, and the screen's one main button. */
function Resume({ task }: { task: Internal.LabelTask }) {
  return (
    <section
      aria-label="Resume"
      className="flex flex-wrap items-center gap-x-10 gap-y-6 rounded-2xl border border-dl-border bg-dl-surface px-7 py-6"
    >
      <div className="flex min-w-0 flex-[999_1_26rem] flex-col gap-2.5">
        <span className="text-sm font-semibold uppercase tracking-wider text-dl-faint">
          Pick up where you left off
        </span>
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="text-2xl font-semibold text-dl-text">
            {task.name}
          </span>
          <RoleChip reviewer={task.reviewer} />
          {task.description && (
            <span className="text-lg text-dl-muted">{task.description}</span>
          )}
        </div>
        <TaskProgress task={task} className="max-w-xl" />
      </div>
      <div className="flex flex-auto flex-wrap items-center justify-end gap-4">
        <Link
          to="/my-label-tasks"
          className="text-lg font-medium text-dl-accent hover:underline"
        >
          All my tasks
        </Link>
        <TaskLink
          task={task}
          className="inline-flex h-11 items-center gap-2 rounded-lg bg-dl-primary px-5 text-xl font-semibold text-white transition hover:bg-dl-primary-hover aria-disabled:bg-dl-sunken aria-disabled:text-dl-faint"
        >
          Continue {task.reviewer ? 'reviewing' : 'labeling'}
          <ArrowRightIcon aria-hidden className="h-[18px] w-[18px]" />
        </TaskLink>
      </div>
    </section>
  );
}

/**
 * The landing page: a greeting, the task to pick up again (the screen's one main action), what waits for the
 * user (to label, to review, done — each figure opens My tasks filtered on it), their open tasks, the most
 * advanced first, and their unread notifications. Everything comes from what viewer-api already serves the
 * worklist.
 */
export function Home() {
  const { user } = useAuth0();
  const { has } = usePermissions();
  const tasks = useMyTasks();
  const notifications = useLastNotifications();

  const all = tasks.data?.content ?? [];
  // what is still to do, the most advanced first (as My tasks sorts by default); a finished task counts apart
  const open = all.filter(task => !isFinished(task)).sort(byProgress);
  const done = all.length - open.length;
  const toLabel = open.filter(task => !task.reviewer);
  const toReview = open.filter(task => task.reviewer);
  const next = open[0];
  const lastUnread = notifications.data?.content ?? [];
  const count = (value: number) => (tasks.isSuccess ? value : '…');
  const name = firstName(user);

  return (
    <div className="mx-auto flex max-w-screen-xl flex-col gap-6">
      <div className="flex flex-col gap-1">
        <p className="text-lg text-dl-muted">
          {new Date().toLocaleDateString(DATE_LOCALE, {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
          })}
        </p>
        <h1 className="text-[30px] font-semibold leading-tight tracking-tight text-dl-text">
          {greeting()}
          {name && `, ${name}`}
        </h1>
        {tasks.isSuccess && all.length === 0 && (
          <p className="text-lg text-dl-muted">
            No task is assigned to you yet.
          </p>
        )}
      </div>

      {next && <Resume task={next} />}

      <div className="grid grid-cols-[repeat(auto-fit,minmax(12.5rem,1fr))] gap-4">
        <Stat
          label="To label"
          value={count(toLabel.length)}
          icon={PencilSquareIcon}
          tone="bg-dl-tint text-dl-primary"
          to="/my-label-tasks?role=labeling&status=todo"
        />
        <Stat
          label="To review"
          value={count(toReview.length)}
          icon={CheckBadgeIcon}
          tone="bg-dl-review-tint text-dl-review"
          to="/my-label-tasks?role=review&status=todo"
        />
        <Stat
          label="Done"
          value={count(done)}
          icon={CheckCircleIcon}
          tone="bg-dl-success-tint text-dl-success"
          to="/my-label-tasks?status=done"
        />
        <Stat
          label="Assigned in total"
          value={count(all.length)}
          icon={ClipboardDocumentListIcon}
          tone="bg-dl-sunken text-dl-muted"
          to="/my-label-tasks"
        />
      </div>

      <div className="flex flex-wrap items-start gap-6">
        <div className="min-w-0 flex-[999_1_35rem]">
          <Panel
            title="Your tasks"
            flush
            action={
              <SeeAll to="/my-label-tasks">
                {tasks.isSuccess ? `See all ${all.length}` : 'See all'}
              </SeeAll>
            }
          >
            {tasks.isError && (
              <p className="px-6 py-4 text-dl-danger">
                Could not load your tasks.
              </p>
            )}
            {tasks.isSuccess && all.length === 0 && (
              <p className="px-6 py-4 text-lg text-dl-muted">
                Nothing assigned yet: a data manager adds you to a task.
              </p>
            )}
            {tasks.isSuccess && all.length > 0 && open.length === 0 && (
              <p className="px-6 py-4 text-lg text-dl-muted">
                Every task assigned to you is finished.
              </p>
            )}
            <ul>
              {open.slice(0, 5).map(task => (
                <li
                  key={task.taskId}
                  className="group relative flex flex-wrap items-center gap-x-5 gap-y-3 border-b border-dl-border px-6 py-4 last:border-b-0 hover:bg-dl-raised"
                >
                  <span className="flex min-w-0 flex-[999_1_17rem] flex-col gap-1.5">
                    <span className="flex min-w-0 items-center gap-2.5">
                      <span className="truncate text-xl font-semibold text-dl-text">
                        {task.name}
                      </span>
                      <RoleChip reviewer={task.reviewer} />
                    </span>
                    <span className="font-mono text-sm text-dl-faint">
                      #{task.taskId} · created {formatDay(task.createdAt)}
                    </span>
                    <TaskProgress task={task} className="max-w-xs" />
                  </span>
                  <TaskAction task={task} variant="outline" stretched />
                </li>
              ))}
            </ul>
          </Panel>
        </div>

        <div className="flex min-w-0 flex-[1_1_18rem] flex-col gap-6">
          <Panel title="Notifications" action={<SeeAll to="/notifications" />}>
            {lastUnread.length === 0 ? (
              <p className="flex items-center gap-3 text-lg text-dl-muted">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-dl-success-tint text-dl-success">
                  <CheckIcon aria-hidden className="h-4 w-4" />
                </span>
                You are all caught up.
              </p>
            ) : (
              <ul className="flex flex-col gap-3">
                {lastUnread.map(notification => (
                  <li key={notification.id} className="flex gap-3">
                    <span
                      aria-hidden
                      className="mt-2 h-2 w-2 shrink-0 rounded-full bg-dl-primary"
                    />
                    <span className="min-w-0">
                      <span className="line-clamp-2 block text-lg text-dl-text">
                        {notification.text}
                      </span>
                      <span className="block text-sm text-dl-faint">
                        {formatDay(notification.createdAt)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel title="Shortcuts">
            <div className="-mx-3 flex flex-col gap-1">
              <Link to="/ohif/local" className={shortcut}>
                <FolderOpenIcon aria-hidden className="h-5 w-5" />
                View local files
              </Link>
              {BACKOFFICE_PERMISSIONS.some(has) && (
                <Link to="/backoffice" className={shortcut}>
                  <Squares2X2Icon aria-hidden className="h-5 w-5" />
                  Backoffice
                </Link>
              )}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
